import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignComparableModel, CampaignComparableRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignComparables']

type CampaignComparableFilters = {
  campaign_id?: number,
  comparable_source?: 'agency' | 'market',
}

type UseCampaignComparablesOptions = {
  filters?: CampaignComparableFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignComparableRequestOptions,
}

function useCampaignComparables(options: UseCampaignComparablesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_id: filterCampaignId,
    comparable_source: filterCampaignSource,
  } = filters

  const {
    updatedEntities: { campaignComparables: campaignComparablesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignComparables } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignComparables = useMemo(() => {
    const filtered = Object.values(campaignComparables).filter((campaignComparable: CampaignComparableModel) => {
      const { campaign_id, comparable_source } = campaignComparable

      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchCampaignSource = filterCampaignSource ? comparable_source === filterCampaignSource : true

      return matchCampaignId && matchCampaignSource
    })

    return sortArrayBy(filtered, 'asc')
  }, [campaignComparablesUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignComparablesCount = filteredCampaignComparables.length
  const hasFilteredCampaignComparables = !!filteredCampaignComparablesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignComparablesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignComparables } = useReduxAction(
    'campaignComparables',
    'loadCampaignComparables',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    campaignComparablesUpdatedAt,
    canLoadMore,
    filteredCampaignComparables,
    filteredCampaignComparablesCount,
    hasFilteredCampaignComparables,
    loading: loadingCampaignComparables,
  }
}

export default useCampaignComparables
